import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useWireState, useWireValue } from '@forminator/react-wire'
import { toast } from 'react-toastify'
import JSZip from 'jszip'
import * as store from '@store'
import * as actions from '@actions'
import {
    DOC_CAN_PROCESS_STATUSES,
    ORG_DOCS_CAN_PROCESS_STATUSES,
    DOCUMENT_STATUS_MODEL_APPLIED,
    DOC_NOT_READY_STATUSES,
    EVENT_COLLECTION_DOCS_UPLOADED,
} from '@constants'
import useUserHasRoles from '@hook/useUserHasRoles'
import { useAccessUser } from '@hook/useAccessUser'
import { useDisabledHover } from '@hook/useDisabledHover'
import { useRealtimeDocumentsStatus } from '@hook/useRealtimeDocumentsStatus'
import usePayments from '@hook/usePayments'
import useSortableTableHeaders from '@hook/useSortableTableHeaders'
import { ROLE_PARSE_ADMIN } from '@constants/roles'
import { PURCHASE_TYPE_PROCESS_DOCUMENT, PURCHASE_TYPE_VIEW_INDEX_DOCUMENT } from '@/constants'
import { createSyntheticDownload } from '@/utils'
import { usePollDocumentsStatus } from '@/hook/usePollDocumentsStatus'
import useEventSubscription from '@hook/useEventSubscription'
import { useQuery } from '@tanstack/react-query'
import useSearchParamsEx from '@hook/useSearchParamsEx'

const ITEMS_PER_PAGE = 100

/**
 * this is horrible, but left the keys for reference
 *
 * 0: key
 * 1: label
 * 2: className
 * 3: style
 * 4: sortable
 * 5: shouldShow
 */
const headers = [
    ['name', 'Name', undefined, undefined, true],
    ['documentType', 'Document Type', 'hidden md:table-cell', '150px', true,
        isParseCollection => !isParseCollection],
    ['templateName', 'Template', 'hidden md:table-cell', '150px', false,
        (isParseCollection, isParseAdmin) => isParseAdmin && !isParseCollection],
    ['createdAt', 'Created', 'hidden md:table-cell', '150px', true],
    // disabled just for now, bc doesn't working properly
    ['reviewStatus', 'Status', '!cursor-default', '120px', false],
]

const DocumentsViewModel = ({
    collectionId,
    openRunsheetId,
    markedDocuments,
    setMarkedDocuments,
}) => {
    
    const location = useLocation()
    const [_searchParams, setSearchParams] = useSearchParams({ q: '' })
    const { q, doneStatus, currentStatus } = useSearchParamsEx(_searchParams, ['q', 'doneStatus', 'currentStatus'])
    
    const uploadRef = createRef()
    const firstLoadRef = useRef(true)
    
    const [pageNumber, setPageNumber] = useState(0)
    const [updateDocs, setUpdateDocs] = useState(false)
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
    const [uploadData, setUploadData] = useState([])
    const [showDeletePopup, setShowDeletePopup] = useState(false)
    const [loadingProcessDocuments, setLoadingProcessDocuments] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    
    const remoteConfig = useWireValue(store.remoteConfig)
    const currentCollection = useWireValue(store.currentCollection)
    const [documents, setDocuments] = useWireState(store.documents)
    
    const templateName = useMemo(() => (
        documents.collectionTemplate || documents.organizationTemplate || null
    ), [documents])
    
    const inheritedFrom = useMemo(() => (
        documents.collectionTemplate
            ? 'Collection' : documents.organizationTemplate ? 'Organization' : null
    ), [documents])
    
    const isParseAdmin = useUserHasRoles([
        ROLE_PARSE_ADMIN,
        
        // ROLE_SUBSCRIPTION_ADMIN, // @todo left it here because might be back live in the future
    ])
    
    const onlyAdmin = useUserHasRoles([ROLE_PARSE_ADMIN])
    
    const canDeleteDocuments = useMemo(() => {
        return currentCollection && ((currentCollection.isParse && onlyAdmin) || !currentCollection.isParse)
    }, [currentCollection, onlyAdmin])
    
    const {
        orderField,
        orderDirection,
        onHeaderClick,
    } = useSortableTableHeaders(headers)
    
    const userFileUpload = useAccessUser({
        accessType: 'collections',
        accessName: 'upload_user_data',
    })
    
    const uploadFile = useDisabledHover(!userFileUpload)
    
    const payments = usePayments()
    
    const resetValues = () => {
        setDocuments({})
    }
    
    const exportAnnotations = async () => {
        
        setExporting(true)
        
        const res = await actions.exportDocumentsAnnotations(collectionId)
        
        if (res)
            createSyntheticDownload(res.data, res.headers['content-type'], res.headers['content-name'])
        else
            toast.error('Failed to export Collection Annotations, please try again.')
        
        setExporting(false)
        
    }
    
    const download = async () => {
        
        setIsDownloading(true)
        
        try {
            
            const zip = new JSZip()
            
            for (const documentId of markedDocuments) {
                try {
                    const document = documents.rows.find(doc => doc.id === documentId)
                    const documentName = document ? document.name : `document_${documentId}.pdf`
                    
                    const res = await actions.downloadDocumentPdf(documentId)
                    
                    if (res?.data)
                        zip.file(documentName, res.data)
                } catch (err) {
                    console.error(`Error downloading documentId ${documentId}`, err)
                }
            }
            
            const content = await zip.generateAsync({ type: 'blob' })
            
            createSyntheticDownload(content, 'application/zip', currentCollection.name)
            
        } catch (e) {
            
            console.error('download err', e)
            toast.error(`Please ensure you have selected a document and try again. 
                If this issue persists, please contact customer support.`)
            
        } finally {
            setIsDownloading(false)
        }
    }
    
    const handleDownload = async () => {
        
        if (currentCollection.isParse && payments.showPurchaseDialog({
            purchaseType: PURCHASE_TYPE_VIEW_INDEX_DOCUMENT,
            itemCount: markedDocuments.length,
            onConfirmPayment: download,
        })) return
        
        download()
    }
    
    const updateSearchParams = params => setSearchParams(prev => {
        
        const newSearchParams = new URLSearchParams(prev)
        
        Object.keys(params).forEach(key => {
            
            if (params[key] === undefined)
                newSearchParams.delete(key)
            else
                newSearchParams.set(key, params[key])
            
        })
        
        return newSearchParams
        
    })
    
    const updateSearchParam = (key, value) => updateSearchParams({ [key]: value })
    
    const {
        error,
        isLoading,
        refetch: fetchDocuments,
    } = useQuery({
        queryKey: [
            'documents',
            collectionId,
            pageNumber,
            q,
            doneStatus,
            currentStatus,
            orderField,
            orderDirection,
            updateDocs,
        ],
        queryFn: () => {
            
            return actions.fetchDocuments({
                collectionId,
                offset: pageNumber * ITEMS_PER_PAGE,
                limit: ITEMS_PER_PAGE,
                queryValue: q,
                doneStatus: doneStatus,
                currentStatus: currentStatus,
                orderField: orderField,
                orderDirection: orderDirection,
            })
            
        },
        retry: 0,
        keepPreviousData: true,
    })
    
    const onDrop = async files => {
        try {
            const validFiles = files.filter(it => it.name?.length)
            
            if (validFiles.length < 1) return
            
            setUploadDialogOpen(true)
            setUploadData(validFiles)
        } catch (e) {
            console.error('onDrop failed', e)
        }
    }
    
    const safeProcessDocument = async (documentId, batchSize, fundingSource) => {
        
        try {
            await actions.queueDocumentForProcessing(documentId, undefined, batchSize, fundingSource)
            await actions.fetchDocumentById(documentId)
            return true
        } catch (e) {
            return false
            // NOOP
        }
    }
    
    const onResetDocumentsClick = useCallback(async () => {
        
        const selectedDocuments = markedDocuments.map(id => documents.rows.find(doc => doc.id === id))
        
        const resetDocuments = selectedDocuments.filter(it => documents.rows.some(doc => (
            doc.id === it.id &&
            DOCUMENT_STATUS_MODEL_APPLIED.includes(doc.orgStatus || doc.reviewStatus)),
        ))
        
        if (!resetDocuments?.length) {
            toast.warn('All selected documents have already been reset or are not ready to be reset')
            return console.warn('No qualifying documents found for reset')
        }
        
        await Promise.all(resetDocuments.map(doc => actions.resetDocumentStatus(doc.id)))
        toast.success(`Reset status for ${resetDocuments.length}` +
            `${resetDocuments.length === 1 ? ' document' : ' documents'}`)
        
        setMarkedDocuments([])
        
        await fetchDocuments()
        
    }, [documents, markedDocuments])
    
    const onProcessDocumentsClick = useCallback(async () => {
        
        const selectedDocuments = markedDocuments.map(id => documents.rows.find(doc => doc.id === id))
        
        // Only process documents that haven't been processed yet
        const qualifiedDocs = selectedDocuments.filter(it => documents.rows.some(doc => (
            doc.id === it.id &&
            DOC_CAN_PROCESS_STATUSES.includes(doc.orgStatus || doc.reviewStatus)
            && (!doc.orgStatus || ORG_DOCS_CAN_PROCESS_STATUSES.includes(doc.orgStatus))
        )))
        
        if (!qualifiedDocs?.length) {
            toast.warn('All selected documents have already been processed or are not ready for processing')
            return console.warn('No qualifying documents found for processing')
        } else if (qualifiedDocs.length !== selectedDocuments.length) {
            toast.warn(`${selectedDocuments.length - qualifiedDocs.length} of ${selectedDocuments.length}` +
                ' selected documents have already been processed or is not ready to be processed')
        }
        
        // We can skip the check here, since processing always incurs a fee
        if (payments.showPurchaseDialog({
            purchaseType: PURCHASE_TYPE_PROCESS_DOCUMENT,
            itemCount: qualifiedDocs.length,
            onConfirmPayment: processDocuments(qualifiedDocs),
        })) return
        
        await processDocuments(qualifiedDocs)()
        
    }, [documents, markedDocuments])
    
    const onDeleteDocumentsClick = useCallback(async () => {
        
        try {
            await actions.deleteDocuments(markedDocuments)
            toast.success(`Deleted ${markedDocuments.length}` +
                `${markedDocuments.length === 1 ? ' document' : ' documents'} successfully`)
            await fetchDocuments()
            setMarkedDocuments([])
            
        } catch (e) {
            // NOOP
        }
    }, [markedDocuments])
    
    const processDocuments = qualifiedDocs => async (remainingBalance, fundingSource) => {
        
        setLoadingProcessDocuments(true)
        
        const processStatus = await Promise.all(
            qualifiedDocs.map(it => safeProcessDocument(it.id, qualifiedDocs.length, fundingSource)))
        
        const failedDocs = processStatus.filter(d => !d)
        const successDocs = processStatus.filter(d => d)
        
        if (failedDocs.length)
            toast.error(`Failed to process ${failedDocs.length} ${failedDocs.length === 1 ? 'document' : 'documents'}`)
        
        if (successDocs.length)
            toast.success(`Queued ${successDocs.length}` +
                `${successDocs.length === 1 ? ' document' : ' documents'} for processing`)
        
        if (!remoteConfig?.realtimeEnabled) await actions.updateLocalCredits()
        
        setLoadingProcessDocuments(false)
    }
    
    useEventSubscription(EVENT_COLLECTION_DOCS_UPLOADED, () => {
        
        setPageNumber(0)
        fetchDocuments(0)
        
    }, [])
    
    useEffect(() => {
        /* setSearchParams(prev => {
            prev.set('orderField', orderField)
            prev.set('orderDirection', orderDirection)
            return prev
        }) */
        updateSearchParams({
            orderField,
            orderDirection,
        })
    }, [orderDirection, orderField])
    
    useEffect(() => {
        
        if (!openRunsheetId?.length) return
        
        // If the user opens or refreshes the page with a runsheet ID, automatically fetch & open it
        actions.fetchRunsheetById(openRunsheetId)
            .catch(e => console.error(`Failed to auto fetch runsheet with ID "${openRunsheetId}"`, e))
        
    }, [openRunsheetId])
    
    useEffect(() => {
        // prevent this to call fetch documents if its first screen load
        if (firstLoadRef.current) return
        
        fetchDocuments()
        
    }, [pageNumber, collectionId])
    
    useEffect(() => {
        if (!updateDocs) return
        
        /* setSearchParams(prev => {
            prev.set('q', '')
            prev.delete('currentStatus')
            prev.delete('doneStatus')
            
            return prev
        }) */
        updateSearchParams({
            q: '',
            currentStatus: undefined,
            doneStatus: undefined,
        })
        
        setUpdateDocs(false)
    }, [updateDocs])
    
    useRealtimeDocumentsStatus('collection')
    usePollDocumentsStatus(documents?.rows
        ?.filter(it => DOC_NOT_READY_STATUSES.includes(it.orgStatus || it.reviewStatus))
        ?.map(it => ({ id: it.id, reviewStatus: (it.orgStatus || it.reviewStatus) })) || [])
    
    useEffect(() => {
        return () => resetValues()
    }, [location.pathname, collectionId])
    
    useEffect(() => {
        
        if (currentCollection && !currentCollection.isParse)
            actions.getTemplates()
        
    }, [currentCollection])
    
    useEffect(() => {
        resetValues()
        firstLoadRef.current = false
    }, [])
    
    return {
        
        // Constants
        ITEMS_PER_PAGE,
        headers,
        
        // State
        error,
        isLoading,
        pageNumber,
        setPageNumber,
        updateDocs,
        setUpdateDocs,
        uploadDialogOpen,
        setUploadDialogOpen,
        uploadData,
        setUploadData,
        showDeletePopup,
        setShowDeletePopup,
        loadingProcessDocuments,
        setLoadingProcessDocuments,
        exporting,
        setExporting,
        isDownloading,
        setIsDownloading,
        
        // Global State
        remoteConfig,
        currentCollection,
        documents,
        setDocuments,
        
        // Memos
        templateName,
        inheritedFrom,
        canDeleteDocuments,
        
        // Refs
        uploadRef,
        firstLoadRef,
        
        // Hooks
        location,
        searchParams: _searchParams,
        setSearchParams,
        isParseAdmin,
        onlyAdmin,
        orderField,
        orderDirection,
        onHeaderClick,
        userFileUpload,
        uploadFile,
        payments,
        
        // Methods
        resetValues,
        exportAnnotations,
        download,
        handleDownload,
        updateSearchParam,
        fetchDocuments,
        onDrop,
        safeProcessDocument,
        onResetDocumentsClick,
        onProcessDocumentsClick,
        onDeleteDocumentsClick,
        processDocuments,
        
    }
    
}

export default DocumentsViewModel
